import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../api/client';
import MediaPicker from '../../components/admin/MediaPicker';

const emptyProduct = {
  name: '',
  description: '',
  buttonLink: '',
  overlayImage: '',
  cost: 'Free',
  location: 'Twitch',
  tags: '',
  featured: false,
  buttonText: 'Download Free',
  sortOrder: 0,
};

function isPremium(cost) {
  const value = (cost || '').toLowerCase();
  return value.includes('premium') || value.startsWith('$');
}

export default function AdminTools() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyProduct);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  const [formOpen, setFormOpen] = useState(false);

  const load = () => apiGet('/products').then(setProducts);

  useEffect(() => {
    load().catch(console.error);
  }, []);

  const stats = useMemo(() => {
    const featured = products.filter((p) => p.featured).length;
    const premium = products.filter((p) => isPremium(p.cost)).length;
    return {
      total: products.length,
      featured,
      free: products.length - premium,
      premium,
    };
  }, [products]);

  const sortedProducts = useMemo(
    () => [...products].sort((a, b) => (b.sortOrder || 0) - (a.sortOrder || 0) || a.name.localeCompare(b.name)),
    [products],
  );

  const resetForm = () => {
    setForm(emptyProduct);
    setEditingId(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage('');
    const payload = {
      ...form,
      tags: typeof form.tags === 'string' ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : form.tags,
    };
    try {
      if (editingId) {
        await apiPut(`/products/${editingId}`, payload);
        setMessage('Tool updated.');
      } else {
        await apiPost('/products', payload);
        setMessage('Tool created.');
      }
      resetForm();
      setFormOpen(false);
      load();
    } catch (err) {
      setMessage(err.message);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      description: p.description,
      buttonLink: p.buttonLink,
      overlayImage: p.overlayImage,
      cost: p.cost,
      location: p.location,
      tags: (p.tags || []).join(', '),
      featured: p.featured,
      buttonText: p.buttonText,
      sortOrder: p.sortOrder || 0,
    });
    setFormOpen(true);
  };

  const startAdd = () => {
    resetForm();
    setFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tool?')) return;
    await apiDelete(`/products/${id}`);
    if (editingId === id) {
      resetForm();
      setFormOpen(false);
    }
    load();
  };

  const toggleFeatured = async (p) => {
    await apiPut(`/products/${p.id}`, { ...p, featured: !p.featured });
    load();
  };

  return (
    <div className="admin-catalog-page">
      <div className="admin-catalog-toolbar">
        <div className="admin-catalog-toolbar-row">
          <div className="admin-catalog-stats">
            <div className="admin-catalog-stat-chip">
              <span className="admin-catalog-stat-value">{stats.total}</span>
              <span className="admin-catalog-stat-label">Total</span>
            </div>
            <div className="admin-catalog-stat-chip admin-catalog-stat-chip--featured">
              <span className="admin-catalog-stat-value">{stats.featured}</span>
              <span className="admin-catalog-stat-label">Featured</span>
            </div>
            <div className="admin-catalog-stat-chip admin-catalog-stat-chip--free">
              <span className="admin-catalog-stat-value">{stats.free}</span>
              <span className="admin-catalog-stat-label">Free</span>
            </div>
            <div className="admin-catalog-stat-chip admin-catalog-stat-chip--premium">
              <span className="admin-catalog-stat-value">{stats.premium}</span>
              <span className="admin-catalog-stat-label">Premium</span>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-primary admin-catalog-form-toggle"
            onClick={() => {
              if (formOpen && !editingId) {
                setFormOpen(false);
              } else {
                startAdd();
              }
            }}
          >
            <i className="fas fa-plus" aria-hidden="true" />
            {formOpen && !editingId ? 'Hide' : 'Add tool'}
          </button>
        </div>
      </div>

      {formOpen && (
        <form className="admin-catalog-form" onSubmit={handleSave}>
          {message && <p className="admin-message admin-catalog-form-message">{message}</p>}
          <div className="admin-catalog-form-grid">
            <label className="admin-catalog-field admin-catalog-field--wide">
              <span>Name</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </label>
            <label className="admin-catalog-field">
              <span>Cost</span>
              <input value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
            </label>
            <label className="admin-catalog-field">
              <span>Location</span>
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </label>
            <label className="admin-catalog-field">
              <span>Sort order</span>
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
              />
            </label>
            <label className="admin-catalog-field admin-catalog-field--checkbox">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
              />
              <span>Featured on home</span>
            </label>
            <label className="admin-catalog-field admin-catalog-field--full">
              <span>Description</span>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
              />
            </label>
            <label className="admin-catalog-field">
              <span>Button link</span>
              <input value={form.buttonLink} onChange={(e) => setForm({ ...form, buttonLink: e.target.value })} />
            </label>
            <label className="admin-catalog-field">
              <span>Button text</span>
              <input value={form.buttonText} onChange={(e) => setForm({ ...form, buttonText: e.target.value })} />
            </label>
            <MediaPicker
              label="Image"
              value={form.overlayImage}
              onChange={(url) => setForm({ ...form, overlayImage: url })}
            />
            <label className="admin-catalog-field">
              <span>Tags</span>
              <input
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="comma-separated"
              />
            </label>
          </div>
          <div className="admin-catalog-form-actions">
            {editingId && (
              <button type="button" className="btn btn-secondary" onClick={() => { resetForm(); setFormOpen(false); }}>
                Cancel
              </button>
            )}
            <button type="submit" className="btn btn-primary">
              {editingId ? 'Update tool' : 'Create tool'}
            </button>
          </div>
        </form>
      )}

      <div className="admin-catalog-list-wrap">
        {sortedProducts.length === 0 ? (
          <div className="admin-catalog-empty">
            <i className="fas fa-toolbox" aria-hidden="true" />
            <p>No tools in the catalog yet.</p>
            <button type="button" className="btn btn-primary" onClick={startAdd}>
              Add first tool
            </button>
          </div>
        ) : (
          <ul className="admin-catalog-list">
            {sortedProducts.map((p) => (
              <li
                key={p.id}
                className={`admin-catalog-item ${editingId === p.id ? 'is-editing' : ''}`}
              >
                {p.overlayImage ? (
                  <img src={p.overlayImage} alt="" className="admin-catalog-thumb" />
                ) : (
                  <span className="admin-catalog-thumb is-empty" aria-hidden="true">
                    <i className="fas fa-cube" />
                  </span>
                )}
                <div className="admin-catalog-item-main">
                  <div className="admin-catalog-item-head">
                    <span className="admin-catalog-item-name">{p.name}</span>
                    <span className="admin-catalog-item-sort">#{p.sortOrder || 0}</span>
                  </div>
                  {p.description && (
                    <p className="admin-catalog-item-desc">{p.description}</p>
                  )}
                  <div className="admin-catalog-item-meta">
                    <span className={`admin-catalog-pill ${isPremium(p.cost) ? 'is-premium' : 'is-free'}`}>
                      {p.cost || 'Free'}
                    </span>
                    <span className="admin-catalog-pill">{p.location || '—'}</span>
                  </div>
                </div>
                <div className="admin-catalog-item-actions">
                  <button
                    type="button"
                    className={`admin-catalog-icon-btn admin-catalog-featured-btn ${p.featured ? 'is-active' : ''}`}
                    onClick={() => toggleFeatured(p)}
                    title={p.featured ? 'Remove from featured' : 'Mark as featured'}
                  >
                    <i className={`${p.featured ? 'fas' : 'far'} fa-star`} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="admin-catalog-icon-btn"
                    onClick={() => startEdit(p)}
                    title="Edit"
                  >
                    <i className="fas fa-pen" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="admin-catalog-icon-btn danger"
                    onClick={() => handleDelete(p.id)}
                    title="Delete"
                  >
                    <i className="fas fa-trash" aria-hidden="true" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
